/**
 * @file Labs close-intercept modal helpers.
 */

import {
  NATIVE_INPUT_CLASSES,
  bindNativeFieldBehavior,
  getFieldLabelHtml,
  validateNativeFormFields,
} from "../../shared/forms/nativeFieldStyles.js";
import { formatCurrency } from "../../shared/utils/displayUtils.js";

const MODAL_ID = "labs-close-modal";

function ensureModal() {
  let modal = document.getElementById(MODAL_ID);
  if (modal) {return modal;}

  modal = document.createElement("div");
  modal.id = MODAL_ID;
  modal.className = "fixed inset-0 z-50 hidden items-center justify-center bg-black/40 p-4";
  modal.innerHTML = `
    <div class="w-full max-w-md rounded-lg bg-white shadow-xl">
      <form data-labs-close-form>
        <div class="flex items-center justify-between gap-3 border-b border-gray-200 px-5 py-4">
          <h2 class="text-lg font-semibold text-gray-900">Close External Lab Item</h2>
          <button type="button" class="labs-close-cancel standard-action-icon-btn standard-action-icon-btn--danger shrink-0" aria-label="Cancel close">
            <span class="material-symbols-outlined text-[20px] leading-none">close</span>
          </button>
        </div>
        <div class="space-y-4 px-5 py-4">
          <div class="min-w-0 space-y-1">
            ${getFieldLabelHtml("Cost", true)}
            <input type="text" name="cost" class="${NATIVE_INPUT_CLASSES} currency-input" placeholder="$" inputmode="decimal" required>
          </div>
        </div>
        <div class="flex justify-end gap-3 border-t border-gray-200 px-5 py-4">
          <button type="button" class="labs-close-cancel inline-flex h-9 items-center justify-center rounded-md bg-gray-200 px-4 text-sm font-semibold text-gray-800 hover:bg-gray-300">Cancel</button>
          <button type="submit" class="inline-flex h-9 items-center justify-center rounded-md bg-[var(--dfc-brand-primary)] px-4 text-sm font-semibold text-[var(--dfc-brand-primary-contrast)] hover:bg-[var(--dfc-brand-primary-hover)]">Close Lab Item</button>
        </div>
      </form>
    </div>
  `;
  document.body.append(modal);
  return modal;
}

function normalizeCurrencyInput(value) {
  if (value === null || value === undefined || value === "") {return null;}
  const number = typeof value === "number" ?
    value :
    Number(String(value).replace(/[$,\s]/g, ""));
  return Number.isFinite(number) ? number : null;
}

function bindCloseCostInput(form) {
  const input = form?.querySelector?.('input[name="cost"]');
  if (!input) {return;}
  if (input.dataset.labsCloseCostBound === "true") {return;}
  input.dataset.labsCloseCostBound = "true";
  input.addEventListener("input", (event) => {
    let value = event.target.value.replace(/[^0-9.]/g, "");
    if (value.split(".").length > 2) {
      value = value.substring(0, value.lastIndexOf("."));
    }
    input.dataset.currencyRawValue = value;
    event.target.value = value;
  });
  input.addEventListener("focus", () => {
    const rawValue = input.dataset.currencyRawValue || "";
    input.value = rawValue || "";
  });
  input.addEventListener("blur", () => {
    const rawValue = input.dataset.currencyRawValue || "";
    const formatted = formatCurrency(rawValue);
    input.value = formatted || rawValue || "";
  });
}

/**
 * Returns whether the External Lab close modal should open.
 * @param {object} params Check params.
 * @param {object} params.item Lab item candidate.
 * @param {object} params.config Runtime config.
 * @returns {boolean} True when cost is required and missing.
 */
export function shouldOpenLabsCloseModal({item = {}, config = {}} = {}) {
  const closeStatusId = config?.labs?.closeInterceptStatusId || "";
  return item?.stream === "external" &&
    item?.status === closeStatusId &&
    normalizeCurrencyInput(item?.cost) === null;
}

/**
 * Opens the External Lab cost intercept modal.
 * @param {object} params Modal params.
 * @param {object} params.item Lab item candidate.
 * @param {Function} params.onCancel Cancel callback.
 * @param {Function} params.onSubmit Submit callback.
 * @returns {void}
 */
export function openLabsCloseModal({
  item = {},
  onCancel,
  onSubmit,
} = {}) {
  const modal = ensureModal();
  const form = modal.querySelector("[data-labs-close-form]");
  const costInput = form?.querySelector('input[name="cost"]');
  const close = () => {
    modal.classList.add("hidden");
    modal.classList.remove("flex");
  };

  if (costInput) {
    costInput.value = formatCurrency(item?.cost) || "";
    costInput.dataset.currencyRawValue = item?.cost ?? "";
  }

  modal.querySelectorAll(".labs-close-cancel").forEach((button) => {
    button.onclick = () => {
      close();
      onCancel?.();
    };
  });
  form.onsubmit = async (event) => {
    event.preventDefault();
    if (!validateNativeFormFields(form)) {return;}
    const cost = costInput?.dataset?.currencyRawValue ||
      String(normalizeCurrencyInput(costInput?.value) ?? "");
    const submitButton = form.querySelector('button[type="submit"]');
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = "Closing...";
    }
    try {
      await onSubmit?.({cost});
      close();
    } finally {
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = "Close Lab Item";
      }
    }
  };

  bindNativeFieldBehavior(form);
  bindCloseCostInput(form);
  modal.classList.remove("hidden");
  modal.classList.add("flex");
  requestAnimationFrame(() => costInput?.focus());
}
